import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';

const AdvancedStats = () => {
    const { language } = useLanguage();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedLevel, setSelectedLevel] = useState('ppl');

    useEffect(() => {
        const loadStats = async () => {
            try {
                const data = await api.getStats(selectedLevel);
                setStats(data);
            } catch (error) {
                console.error("Failed to load stats", error);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, [selectedLevel]);

    if (loading) {
        return <div className="p-8 text-center text-gray-500">{language === 'fr' ? 'Chargement des analyses...' : 'Loading analytics...'}</div>;
    }

    if (!stats) return <div>No data.</div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow-sm">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                        {language === 'fr' ? 'Analyses Détaillées' : 'Advanced Analytics'}
                    </h2>
                    <p className="text-gray-500 mt-1">
                        {language === 'fr' ? 'Identifiez vos points forts et faibles.' : 'Identify your strengths and weaknesses.'}
                    </p>
                </div>
                <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="border border-gray-300 rounded-md shadow-sm px-4 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="ppl">PPL</option>
                    <option value="lapl">LAPL</option>
                </select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Weakest Areas */}
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {language === 'fr' ? 'À Travailler (Points Faibles)' : 'Weakest Areas'}
                    </h3>
                    <div className="space-y-4">
                        {stats.weakestAreas && stats.weakestAreas.map((area, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                                <span className="text-gray-700 font-medium">{area.name}</span>
                                <div className="flex items-center">
                                    <div className="w-32 bg-gray-200 rounded-full h-2.5 mr-3">
                                        <div className="h-2.5 rounded-full bg-red-500" style={{ width: `${area.score}%` }}></div>
                                    </div>
                                    <span className="text-sm font-bold text-red-600">{area.score}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Strongest Areas */}
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {language === 'fr' ? 'Points Forts' : 'Strongest Areas'}
                    </h3>
                    <div className="space-y-4">
                        {stats.strongestAreas && stats.strongestAreas.map((area, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                                <span className="text-gray-700 font-medium">{area.name}</span>
                                <div className="flex items-center">
                                    <div className="w-32 bg-gray-200 rounded-full h-2.5 mr-3">
                                        <div className="h-2.5 rounded-full bg-green-500" style={{ width: `${area.score}%` }}></div>
                                    </div>
                                    <span className="text-sm font-bold text-green-600">{area.score}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Detailed Chart */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-bold text-gray-800 mb-6">
                    {language === 'fr' ? 'Performance par Module' : 'Performance by Module'}
                </h3>
                <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={stats.moduleScores}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                            <XAxis type="number" domain={[0, 100]} />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip cursor={{ fill: 'transparent' }} />
                            <Bar dataKey="score" barSize={20} radius={[0, 4, 4, 0]}>
                                {stats.moduleScores.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.score >= 75 ? '#22c55e' : entry.score >= 50 ? '#f59e0b' : '#ef4444'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default AdvancedStats;
